/**
 * Turns whatever the file picker handed us into something the server will accept.
 *
 * A phone photo is several megabytes, in an orientation recorded in EXIF rather than in
 * the pixels, at a resolution nobody needs. Doing the work here rather than server-side
 * means the upload is tens of kilobytes instead of tens of megabytes, and — more
 * importantly — the server never decodes untrusted image data, which is the part of image
 * handling with the worst security history.
 *
 * Two presets, and the difference between them is not size but *framing*. An avatar is
 * cropped to a square because it is drawn inside a circle and letterboxing would look
 * broken. A broadcast image is not cropped at all: it is usually a graphic somebody
 * composed, and cutting a headline in half to satisfy an aspect ratio would ruin the thing
 * being uploaded.
 */

const QUALITY = 0.85;

export interface PreparedImage {
  mimeType: 'image/webp' | 'image/jpeg';
  /** base64, no `data:` prefix — what the API expects. */
  data: string;
  byteSize: number;
  /** For an immediate local preview, before the round trip finishes. */
  previewUrl: string;
}

interface Options {
  /** Longest edge of the output, in pixels. */
  maxEdge: number;
  /**
   * `cover` crops to a square. `contain` keeps the original proportions and only scales
   * down — nothing is ever cut off, and a small image is left alone.
   */
  fit: 'cover' | 'contain';
  /** Rejected above this, before we try to decode it. Only guards against a stray video. */
  maxInputBytes: number;
}

const AVATAR: Options = { maxEdge: 256, fit: 'cover', maxInputBytes: 25 * 1024 * 1024 };
const BANNER: Options = { maxEdge: 1080, fit: 'contain', maxInputBytes: 25 * 1024 * 1024 };

/** A square, for the circular mask it will be drawn inside. */
export function prepareAvatar(file: File): Promise<PreparedImage> {
  return prepareImage(file, AVATAR);
}

/**
 * A notification and landing-page image, proportions intact.
 *
 * Android Chrome crops what it draws to its own aspect ratio, so the important part of the
 * picture should be near the middle. Nothing here can fix that — cropping it ourselves
 * would only mean losing the same pixels earlier and also losing them on the landing page,
 * where the whole image is shown.
 */
export function prepareBroadcastImage(file: File): Promise<PreparedImage> {
  return prepareImage(file, BANNER);
}

async function prepareImage(file: File, options: Options): Promise<PreparedImage> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Choose an image file.');
  }
  if (file.size > options.maxInputBytes) {
    throw new Error(
      `That image is too large. Try one under ${Math.round(options.maxInputBytes / (1024 * 1024))}MB.`,
    );
  }

  // `from-image` applies the EXIF rotation, without which portrait phone photos arrive
  // sideways — the single most common way an image upload looks broken.
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' }).catch(() => {
    throw new Error('That file could not be read as an image.');
  });

  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('This browser could not process the image.');
    ctx.imageSmoothingQuality = 'high';

    if (options.fit === 'cover') {
      const size = options.maxEdge;
      canvas.width = size;
      canvas.height = size;
      // Fill the square and crop the overflow, centred.
      const scale = Math.max(size / bitmap.width, size / bitmap.height);
      const width = bitmap.width * scale;
      const height = bitmap.height * scale;
      ctx.drawImage(bitmap, (size - width) / 2, (size - height) / 2, width, height);
    } else {
      // Never upscale: enlarging a small graphic only makes a bigger blurry graphic.
      const scale = Math.min(1, options.maxEdge / Math.max(bitmap.width, bitmap.height));
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    }

    // WebP first for the size, but not every browser can *encode* it — older Safari
    // silently hands back a PNG instead. So the result's own type is trusted over the type
    // that was asked for, and JPEG is the fallback both because it is universal and
    // because a PNG of a photograph is several times larger than either.
    let blob = await toBlob(canvas, 'image/webp');
    let mimeType: PreparedImage['mimeType'] = 'image/webp';
    if (!blob || blob.type !== 'image/webp') {
      blob = await toBlob(canvas, 'image/jpeg');
      mimeType = 'image/jpeg';
    }
    if (!blob) throw new Error('This browser could not encode the image.');

    const buffer = await blob.arrayBuffer();
    return {
      mimeType,
      data: base64(buffer),
      byteSize: buffer.byteLength,
      previewUrl: canvas.toDataURL(mimeType, QUALITY),
    };
  } finally {
    bitmap.close();
  }
}

function toBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, QUALITY));
}

/**
 * Chunked rather than one spread into `String.fromCharCode`, which blows the argument
 * limit and throws on anything but the smallest images.
 */
function base64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}
